import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { client } from '../../api/client';
import layout from '../../../ui/layout';
import user from '../../../profile/user';
import postcodeFormatting from '../../../utils/postcode-formatting';

export const Status = Object.freeze({
	Uninitialised: 'uninitialised',
	Loading: 'loading',
	Succeeded: 'succeeded',
	Failed: 'failed'
});

const defaultDistance = 2000;

const initialState = {
	error: null,
	filterState: {
		encodedQueryState: null,
		resultsUrl: null,
		searchType: null,
		totalResultCount: -1,
		manufacturerItems: [],
		navigatedManufacturer: null,
		rangeItems: [],
		navigatedRange: null,
		modelItems: [],
		navigatedModel: null,
		trimItems: [],
		navigatedTrim: null,
		derivativeItems: [],
		navigatedDerivative: null,
		carTypeItems: [],
		navigatedCarType: null,
		gearboxItems: [],
		navigatedGearbox: null,
		fuelTypeItems: [],
		navigatedFuelType: null,
		mileageItems: [],
		navigatedMileage: null,
		colourItems: [],
		navigatedColour: null,
		numberOfDoorsItems: [],
		navigatedNumberOfDoors: null,
		numberOfSeatsItems: [],
		navigatedNumberOfSeats: null,
		yearItems: [],
		navigatedYear: null,
		engineSizeItems: [],
		navigatedEngineSize: null,
		navigatedPrice: null,
		distance: defaultDistance,
		sortOptions: []
	},
	filterStatus: Status.Uninitialised,
	location: {
		invalid: false,
		latLong: user.getLocation(),
		postcode: postcodeFormatting.format(user.getPostcode() ?? '')
	},
	openFilterName: null,
	results: null,
	resultsStatus: Status.Uninitialised,
	sortOrder: user.getCfsSortOrder(),
	updateResults: false
};

const makeApiData = (state, filterName, filterValue) => {
	const filterState = state.filterState;

	const result = {
		searchType: 'Used',
		manufacturer: filterState.navigatedManufacturer?.value,
		range: filterState.navigatedRange?.value,
		model: filterState.navigatedModel?.value,
		trim: filterState.navigatedTrim?.value,
		derivative: filterState.navigatedDerivative?.value,
		carType: filterState.navigatedCarType?.value,
		gearbox: filterState.navigatedGearbox?.value,
		fuelType: filterState.navigatedFuelType?.value,
		mileage: filterState.navigatedMileage?.value,
		colour: filterState.navigatedColour?.value,
		numberOfDoors: filterState.navigatedNumberOfDoors?.value,
		numberOfSeats: filterState.navigatedNumberOfSeats?.value,
		year: filterState.navigatedYear?.value,
		engineSize: filterState.navigatedEngineSize?.value,
		price: filterState.navigatedPrice?.value,
		distance: filterState.distance,
		postcode: state.location.postcode,
		latLong: state.location.latLong
	};

	result[filterName] = filterValue;

	return result;
};

export const fetchUpdatedFilterState = createAsyncThunk(
	'cfsHubSearch/fetchUpdatedFilterState',
	async (filter, { getState }) => {
		const { cfsHubSearch } = getState();

		const apiData = makeApiData(cfsHubSearch, filter.name, filter.value);

		const response = await client.post(
			'/api/cfs-search/filters/',
			apiData
		);

		return {
			filterState: response.data,
			updateResults: filter.forceResultRefresh || layout.isDesktop()
		};
	},
	{
		condition: (filter, { getState }) => {
			if (filter.name === 'sortOrder') {
				return true;
			}

			const { cfsHubSearch } = getState();
			const { postcode, latLong } = cfsHubSearch.location;

			const isLocationValid = (postcode && postcodeFormatting.isValid(postcode)) || latLong;

			if (!isLocationValid) {
				return false;
			}
		},
		dispatchConditionRejection: true
	}
);

export const fetchInitialState = createAsyncThunk(
	'cfsHubSearch/fetchInitialState',
	async (initialState) => {
		const response = await client.get(
			'/api/cfs-search/initial-filters/' + initialState
		);

		return response.data;
	},
	{
		condition: (filter, { getState }) => {
			const { cfsHubSearch } = getState();

			if (!cfsHubSearch.filterStatus === Status.Uninitialised) {
				return false;
			}
		}
	}
);

export const cfsHubSlice = createSlice({
	name: 'cfsHubSearch',
	initialState,
	reducers: {
		updatePostcode(state, action) {
			state.location.postcode = action.payload;

			if (action.payload) {
				state.location.invalid = false;
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchUpdatedFilterState.pending, (state) => {
				state.filterStatus = Status.Loading;
				state.openFilterName = null;
				state.location.invalid = false;
			})
			.addCase(fetchUpdatedFilterState.fulfilled, (state, action) => {
				const { filterState, updateResults } = action.payload;
				state.filterStatus = Status.Succeeded;
				state.filterState = filterState;
				state.updateResults = updateResults;
			})
			.addCase(fetchUpdatedFilterState.rejected, (state, action) => {
				state.filterStatus = Status.Failed;

				if (action.error.name === 'ConditionError') {
					// Rejected by our location condition - never tried to send
					state.openFilterName = null;
					state.location.invalid = true;
					state.error = 'Invalid location';
				} else {
					state.error = action.error.message;
				}
			});

		builder
			.addCase(fetchInitialState.pending, (state) => {
				state.filterStatus = Status.Loading;
			})
			.addCase(fetchInitialState.fulfilled, (state, action) => {
				state.filterStatus = Status.Succeeded;
				state.filterState = action.payload;
			})
			.addCase(fetchInitialState.rejected, (state, action) => {
				state.filterStatus = Status.Failed;
				state.error = action.error.message;
			});
	}
});

export const { updatePostcode } = cfsHubSlice.actions;

export default cfsHubSlice.reducer;